import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronDown, ChevronUp, Copy, ExternalLink, Loader2 } from "lucide-react";

import { Button, Card, SearchBar, StatusBadge, Tabs } from "../ui";
import { buscarLogsPorParceiroJoin } from "../../services/joinIntegrationService";

const PAGE_SIZE = 20;

const STATUS_FILTROS = [
  { value: "", label: "Todos" },
  { value: "ERROR", label: "Erros" },
  { value: "WARNING", label: "Avisos" },
];

const STATUS_META = {
  SUCCESS: { label: "Sucesso", variant: "success" },
  WARNING: { label: "Aviso", variant: "warning" },
  ERROR: { label: "Erro", variant: "danger" },
  RUNNING: { label: "Executando", variant: "info" },
};

const ORIGEM_LABEL = { API: "API", WEBHOOK: "Webhook", EVOLUCAO: "Evolução" };

// O backend grava em UTC sem fuso ("2026-09-21T14:22:49"): sem o Z, o navegador leria como hora local.
function formatarDataUtc(valor) {
  if (!valor) return "—";
  const texto = typeof valor === "string" && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(valor) ? `${valor}Z` : valor;
  const data = new Date(texto);
  return Number.isNaN(data.getTime()) ? "—" : data.toLocaleString("pt-BR");
}

function formatarPayload(payload) {
  if (!payload) return "";
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload; // cortado por tamanho ou não-JSON: mostra como veio
  }
}

/**
 * Busca geral por parceiro na integração Join: todos os eventos (webhook e API) de um código Join,
 * CPF/CNPJ ou nome, com o payload recebido. Feita para investigar cadastro que não atualiza, como
 * carteira herdada em massa chegando por webhook.
 */
export default function BuscaParceiroJoin() {
  const navigate = useNavigate();
  const [termo, setTermo] = useState("");
  const [status, setStatus] = useState("");
  const [itens, setItens] = useState([]);
  const [pagina, setPagina] = useState({ page: 0, last: true, totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [abertos, setAbertos] = useState(() => new Set());
  const [copiadoId, setCopiadoId] = useState(null);
  const requestSeqRef = useRef(0);

  const termoValido = termo.trim().length >= 2;

  const carregar = async ({ page = 0, append = false } = {}) => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    try {
      const data = await buscarLogsPorParceiroJoin({
        q: termo.trim(),
        status: status || undefined,
        page,
        size: PAGE_SIZE,
      });
      if (seq !== requestSeqRef.current) return;
      const content = Array.isArray(data?.content) ? data.content : [];
      setItens((anteriores) => (append ? [...anteriores, ...content] : content));
      setPagina({ page: data?.page ?? page, last: data?.last ?? true, totalElements: data?.totalElements ?? content.length });
    } catch {
      if (seq !== requestSeqRef.current) return;
      if (!append) setItens([]);
      setPagina({ page: 0, last: true, totalElements: 0 });
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  };

  // Busca sozinha enquanto digita (espera curta para não consultar a cada tecla).
  useEffect(() => {
    if (!termoValido) {
      requestSeqRef.current += 1;
      setItens([]);
      setPagina({ page: 0, last: true, totalElements: 0 });
      setLoading(false);
      return undefined;
    }
    const timeout = window.setTimeout(() => carregar({ page: 0 }), 400);
    return () => window.clearTimeout(timeout);
  }, [termo, status]);

  const togglePayload = (id) => {
    setAbertos((atual) => {
      const next = new Set(atual);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const copiarPayload = async (item) => {
    try {
      await navigator.clipboard.writeText(formatarPayload(item.payload));
      setCopiadoId(item.id);
      window.setTimeout(() => setCopiadoId((atual) => (atual === item.id ? null : atual)), 1500);
    } catch {
      // Sem permissão de área de transferência: o payload continua visível para copiar à mão.
    }
  };

  return (
    <Card title="Buscar parceiro nos eventos da Join">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <SearchBar
          className="w-full lg:max-w-xl"
          placeholder="Código Join, CPF/CNPJ ou nome do parceiro"
          value={termo}
          onChange={(event) => setTermo(event.target.value)}
        />
        <Tabs items={STATUS_FILTROS} value={status} onChange={setStatus} idPrefix="busca-join-status" ariaLabel="Filtrar por status" />
        {loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Buscando" /> : null}
      </div>

      {!termoValido ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Mostra todos os eventos do parceiro (webhook e API), de qualquer execução, com o JSON que a Join enviou.
        </p>
      ) : !loading && itens.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Nenhum evento para “{termo.trim()}”{status ? " com esse status" : ""}. Parceiro que nunca chegou a ser criado só é
          achado pelo código Join.
        </p>
      ) : (
        <div className="mt-4 space-y-2.5">
          <p className="text-xs text-muted-foreground">
            {Number(pagina.totalElements || 0).toLocaleString("pt-BR")} evento(s), do mais recente para o mais antigo.
          </p>

          {itens.map((item) => {
            const meta = STATUS_META[item.status] || { label: item.status || "—", variant: "default" };
            const aberto = abertos.has(item.id);
            return (
              <article key={item.id} className="rounded-xl border border-border p-3.5">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={meta.label} variant={meta.variant} shape="rounded" />
                  <span className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                    {ORIGEM_LABEL[item.origem] || item.origem}
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {item.parceiroNome || item.nome || "Parceiro sem nome"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {item.idJoin ? `ID Join ${item.idJoin}` : "sem ID Join"}
                    {item.documento ? ` · ${item.documento}` : ""}
                  </span>
                  <time className="ml-auto text-xs tabular-nums text-muted-foreground">{formatarDataUtc(item.dataExecucao)}</time>
                </div>

                {item.mensagem ? <p className="mt-2 text-[13px] leading-5 text-foreground/85">{item.mensagem}</p> : null}
                {item.erroMensagem ? (
                  <p className="mt-1.5 rounded-lg bg-destructive/10 px-3 py-2 text-[13px] leading-5 text-destructive">{item.erroMensagem}</p>
                ) : null}

                <div className="-ml-2 mt-2 flex flex-wrap items-center gap-1">
                  {item.payload ? (
                    <button
                      type="button"
                      onClick={() => togglePayload(item.id)}
                      aria-expanded={aberto}
                      className="inline-flex h-7 items-center gap-1 rounded-lg px-2 text-[11px] font-semibold text-muted-foreground transition hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {aberto ? "Ocultar payload" : "Ver payload"}
                      {aberto ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    </button>
                  ) : (
                    <span className="px-2 text-[11px] text-muted-foreground">Sem payload (evento anterior à gravação do JSON)</span>
                  )}
                  {item.parceiroId ? (
                    <button
                      type="button"
                      onClick={() => navigate(`/parceiros/${item.parceiroId}`)}
                      className="inline-flex h-7 items-center gap-1.5 rounded-lg px-2 text-[11px] font-semibold text-foreground transition hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      Abrir parceiro
                      {item.parceiroStatus ? <span className="font-normal text-muted-foreground">({item.parceiroStatus})</span> : null}
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  ) : null}
                </div>

                {aberto && item.payload ? (
                  <div className="relative mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copiarPayload(item)}
                      className="absolute right-2 top-2"
                    >
                      {copiadoId === item.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {copiadoId === item.id ? "Copiado" : "Copiar"}
                    </Button>
                    <pre className="max-h-80 overflow-auto rounded-lg bg-muted p-3 pr-24 text-[12px] leading-5 text-foreground">
                      {formatarPayload(item.payload)}
                    </pre>
                  </div>
                ) : null}
              </article>
            );
          })}

          {!pagina.last ? (
            <div className="flex justify-center pt-1">
              <Button variant="outline" size="sm" onClick={() => carregar({ page: Number(pagina.page || 0) + 1, append: true })} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Carregar mais
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}
